// globalState.ts
interface ListState {
	quotes: {
		page: number;
		filters: string[];
		filtersVersion?: number;
		searchKeywords: string;
		scrollPosition: number;
	};
	orders: {
		page: number;
		filters: string[];
		searchKeywords: string;
		scrollPosition: number;
	};
	purchaseOrders: {
		offset: number;
		filters: string[];
		searchTerm: string;
		scrollPosition: number;
	};
	poGenerate: {
		offset: number;
		keywords: string;
		selectedRows: number[];
		filterByVendorId: number | null;
		scrollPosition: number;
	};
	findRelatedOrders: {
		offset: number;
		styleCode: string;
		startDate: string;
		endDate: string;
		filterQuotes: boolean;
		filterOrders: boolean;
		showDateFilter: boolean;
		scrollPosition: number;
	};
	supplier: {
		searchTerm: string;
		filterInactive: boolean;
		offset: number;
		scrollPosition: number;
	};
	receiving: {
		offset: number;
		selectedFilters: string[];
		searchTerm: string;
		scrollPosition: number;
	};
	commits: {
		offset: number;
		keywords: string;
		partialCommited: boolean;
		fillable: boolean;
		scrollPosition: number;
	};
	availableInventory: {
		activeTab: number;
		offset: number;
		searchTerm: string;
		filterInactive: boolean;
		showAvailabilityByRow: number | null;
		scrollPosition: number;
	};
	inventoryItems: {
		list: {
			offset: number;
			keywords: string;
			allowInactive: boolean;
			scrollPosition: number;
		};
		view: {
			activeStep: number;
			item: any;
			lastSavedState: any;
		};
	};
	binUtility: {
		offset: number;
		keywords: string;
		filterMissingBin: boolean;
		scrollPosition: number;
	};
	sizeCharts: {
		offset: number;
		keywords: string;
		scrollPosition: number;
	};
	colorPalette: {
		offset: number;
		keywords: string;
		scrollPosition: number;
	};
	packageCategory: {
		offset: number;
		keywords: string;
		scrollPosition: number;
	};
	subItemCategory: {
		offset: number;
		keywords: string;
		scrollPosition: number;
	};
	customers: {
		activeTab: number;
		offset: number;
		keywords: string;
		showInactive: boolean;
		scrollPosition: number;
	};
	vendors: {
		activeTab: number;
		offset: number;
		keywords: string;
		showInactive: boolean;
		scrollPosition: number;
	};
	navigation: "forward" | "back" | null;
}

const state: ListState = {
	quotes: {
		page: 0,
		filters: ["INCOMPLETE", "FINAL_CHECK"],
		searchKeywords: "",
		scrollPosition: 0,
	},
	orders: {
		page: 0,
		filters: [],
		searchKeywords: "",
		scrollPosition: 0,
	},
	purchaseOrders: {
		offset: 0,
		filters: [],
		searchTerm: "",
		scrollPosition: 0,
	},
	poGenerate: {
		offset: 0,
		keywords: "",
		selectedRows: [],
		filterByVendorId: null,
		scrollPosition: 0,
	},
	findRelatedOrders: {
		offset: 0,
		styleCode: "",
		startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10),
		endDate: new Date().toISOString().slice(0, 10),
		filterQuotes: false,
		filterOrders: false,
		showDateFilter: false,
		scrollPosition: 0,
	},
	supplier: {
		searchTerm: "",
		filterInactive: false,
		offset: 0,
		scrollPosition: 0,
	},
	receiving: {
		searchTerm: "",
		offset: 0,
		selectedFilters: [],
		scrollPosition: 0,
	},
	commits: {
		offset: 0,
		keywords: "",
		partialCommited: false,
		fillable: false,
		scrollPosition: 0,
	},
	availableInventory: {
		activeTab: 1,
		offset: 0,
		searchTerm: "",
		filterInactive: false,
		showAvailabilityByRow: null,
		scrollPosition: 0,
	},
	inventoryItems: {
		list: {
			offset: 0,
			keywords: "",
			allowInactive: false,
			scrollPosition: 0,
		},
		view: {
			activeStep: 1,
			item: null,
			lastSavedState: null,
		},
	},
	binUtility: {
		offset: 0,
		keywords: "",
		filterMissingBin: false,
		scrollPosition: 0,
	},
	sizeCharts: {
		offset: 0,
		keywords: "",
		scrollPosition: 0,
	},
	colorPalette: {
		offset: 0,
		keywords: "",
		scrollPosition: 0,
	},
	packageCategory: {
		offset: 0,
		keywords: "",
		scrollPosition: 0,
	},
	subItemCategory: {
		offset: 0,
		keywords: "",
		scrollPosition: 0,
	},
	customers: {
		activeTab: 1,
		offset: 0,
		keywords: "",
		showInactive: false,
		scrollPosition: 0,
	},
	vendors: {
		activeTab: 1,
		offset: 0,
		keywords: "",
		showInactive: false,
		scrollPosition: 0,
	},
	navigation: null,
};

const mutations = {
	SET_QUOTES_STATE(state: ListState, payload: any) {
		state.quotes = {
			...state.quotes,
			...payload,
		};
	},
	SET_ORDERS_STATE(state: ListState, payload: any) {
		state.orders = {
			...state.orders,
			...payload,
		};
	},
	SET_NAVIGATION(state: ListState, direction: "forward" | "back" | null) {
		state.navigation = direction;
	},
	CLEAR_QUOTES_STATE(state: ListState) {
		state.quotes = {
			page: 0,
			filters: ["INCOMPLETE", "FINAL_CHECK"],
			searchKeywords: "",
			scrollPosition: 0,
		};
		state.navigation = null;
	},
	CLEAR_ORDERS_STATE(state: ListState) {
		state.orders = {
			page: 0,
			filters: [],
			searchKeywords: "",
			scrollPosition: 0,
		};
		state.navigation = null;
	},
	SET_PURCHASE_ORDERS_STATE(state: ListState, payload: any) {
		state.purchaseOrders = {
			...state.purchaseOrders,
			...payload,
		};
	},
	CLEAR_PURCHASE_ORDERS_STATE(state: ListState) {
		state.purchaseOrders = {
			offset: 0,
			filters: [],
			searchTerm: "",
			scrollPosition: 0,
		};
	},
	SET_PO_GENERATE_STATE(state: ListState, payload: any) {
		state.poGenerate = {
			...state.poGenerate,
			...payload,
		};
	},
	CLEAR_PO_GENERATE_STATE(state: ListState) {
		state.poGenerate = {
			offset: 0,
			keywords: "",
			selectedRows: [],
			filterByVendorId: null,
			scrollPosition: 0,
		};
	},
	SET_FIND_RELATED_ORDERS_STATE(state: ListState, payload: any) {
		state.findRelatedOrders = {
			...state.findRelatedOrders,
			...payload,
		};
	},
	CLEAR_FIND_RELATED_ORDERS_STATE(state: ListState) {
		state.findRelatedOrders = {
			offset: 0,
			styleCode: "",
			startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10),
			endDate: new Date().toISOString().slice(0, 10),
			filterQuotes: false,
			filterOrders: false,
			showDateFilter: false,
			scrollPosition: 0,
		};
	},
	SET_SUPPLIER_STATE(state: ListState, payload: any) {
		state.supplier = {
			...state.supplier,
			...payload,
		};
	},
	CLEAR_SUPPLIER_STATE(state: ListState) {
		state.supplier = {
			searchTerm: "",
			filterInactive: false,
			offset: 0,
			scrollPosition: 0,
		};
	},
	SET_RECEIVING_STATE(state: ListState, payload: any) {
		state.receiving = {
			...state.receiving,
			...payload,
		};
	},
	CLEAR_RECEIVING_STATE(state: ListState) {
		state.receiving = {
			searchTerm: "",
			offset: 0,
			selectedFilters: [],
			scrollPosition: 0,
		};
	},
	SET_COMMITS_STATE(state: ListState, payload: any) {
		state.commits = {
			...state.commits,
			...payload,
		};
	},
	CLEAR_COMMITS_STATE(state: ListState) {
		state.commits = {
			offset: 0,
			keywords: "",
			partialCommited: false,
			fillable: false,
			scrollPosition: 0,
		};
	},
	SET_AVAILABLE_INVENTORY_STATE(state: ListState, payload: any) {
		state.availableInventory = {
			...state.availableInventory,
			...payload,
		};
	},
	CLEAR_AVAILABLE_INVENTORY_STATE(state: ListState) {
		state.availableInventory = {
			activeTab: 1,
			offset: 0,
			searchTerm: "",
			filterInactive: false,
			showAvailabilityByRow: null,
			scrollPosition: 0,
		};
	},
	SET_INVENTORY_ITEMS_LIST_STATE(state: ListState, payload: any) {
		state.inventoryItems.list = {
			...state.inventoryItems.list,
			...payload,
		};
	},
	SET_INVENTORY_ITEMS_VIEW_STATE(state: ListState, payload: any) {
		state.inventoryItems.view = {
			...state.inventoryItems.view,
			...payload,
		};
	},
	CLEAR_INVENTORY_ITEMS_STATE(state: ListState) {
		state.inventoryItems = {
			list: {
				offset: 0,
				keywords: "",
				allowInactive: false,
				scrollPosition: 0,
			},
			view: {
				activeStep: 1,
				item: null,
				lastSavedState: null,
			},
		};
	},
	SET_BIN_UTILITY_STATE(state: ListState, payload: any) {
		state.binUtility = {
			...state.binUtility,
			...payload,
		};
	},
	CLEAR_BIN_UTILITY_STATE(state: ListState) {
		state.binUtility = {
			offset: 0,
			keywords: "",
			filterMissingBin: false,
			scrollPosition: 0,
		};
	},
	SET_SIZE_CHARTS_STATE(state: ListState, payload: any) {
		state.sizeCharts = {
			...state.sizeCharts,
			...payload,
		};
	},
	CLEAR_SIZE_CHARTS_STATE(state: ListState) {
		state.sizeCharts = {
			offset: 0,
			keywords: "",
			scrollPosition: 0,
		};
	},
	SET_COLOR_PALETTE_STATE(state: ListState, payload: any) {
		state.colorPalette = {
			...state.colorPalette,
			...payload,
		};
	},
	CLEAR_COLOR_PALETTE_STATE(state: ListState) {
		state.colorPalette = {
			offset: 0,
			keywords: "",
			scrollPosition: 0,
		};
	},
	SET_PACKAGE_CATEGORY_STATE(state: ListState, payload: any) {
		state.packageCategory = {
			...state.packageCategory,
			...payload,
		};
	},
	CLEAR_PACKAGE_CATEGORY_STATE(state: ListState) {
		state.packageCategory = {
			offset: 0,
			keywords: "",
			scrollPosition: 0,
		};
	},
	SET_SUB_ITEM_CATEGORY_STATE(state: ListState, payload: any) {
		state.subItemCategory = {
			...state.subItemCategory,
			...payload,
		};
	},
	CLEAR_SUB_ITEM_CATEGORY_STATE(state: ListState) {
		state.subItemCategory = {
			offset: 0,
			keywords: "",
			scrollPosition: 0,
		};
	},
	SET_CUSTOMERS_STATE(state: ListState, payload: any) {
		state.customers = {
			...state.customers,
			...payload,
		};
	},
	CLEAR_CUSTOMERS_STATE(state: ListState) {
		state.customers = {
			activeTab: 1,
			offset: 0,
			keywords: "",
			showInactive: false,
			scrollPosition: 0,
		};
	},
	SET_VENDORS_STATE(state: ListState, payload: any) {
		state.vendors = {
			...state.vendors,
			...payload,
		};
	},
	CLEAR_VENDORS_STATE(state: ListState) {
		state.vendors = {
			activeTab: 1,
			offset: 0,
			keywords: "",
			showInactive: false,
			scrollPosition: 0,
		};
	},
};

const actions = {
	saveQuotesListState({ commit }: any, payload: any) {
		commit("SET_QUOTES_STATE", payload);
	},
	saveOrdersListState({ commit }: any, payload: any) {
		commit("SET_ORDERS_STATE", payload);
	},
	setNavigation({ commit }: any, direction: "forward" | "back" | null) {
		commit("SET_NAVIGATION", direction);
	},
	clearQuotesListState({ commit }: any) {
		commit("CLEAR_QUOTES_STATE");
	},
	clearOrdersListState({ commit }: any) {
		commit("CLEAR_ORDERS_STATE");
	},
	savePurchaseOrdersListState({ commit }: any, payload: any) {
		commit("SET_PURCHASE_ORDERS_STATE", payload);
	},
	clearPurchaseOrdersListState({ commit }: any) {
		commit("CLEAR_PURCHASE_ORDERS_STATE");
	},
	savePoGenerateState({ commit }: any, payload: any) {
		commit("SET_PO_GENERATE_STATE", payload);
	},
	clearPoGenerateState({ commit }: any) {
		commit("CLEAR_PO_GENERATE_STATE");
	},
	saveFindRelatedOrdersState({ commit }: any, payload: any) {
		commit("SET_FIND_RELATED_ORDERS_STATE", payload);
	},
	clearFindRelatedOrdersState({ commit }: any) {
		commit("CLEAR_FIND_RELATED_ORDERS_STATE");
	},
	saveSupplierState({ commit }: any, payload: any) {
		commit("SET_SUPPLIER_STATE", payload);
	},
	clearSupplierState({ commit }: any) {
		commit("CLEAR_SUPPLIER_STATE");
	},
	saveReceivingListState({ commit }: any, payload: any) {
		commit("SET_RECEIVING_STATE", payload);
	},
	clearReceivingListState({ commit }: any) {
		commit("CLEAR_RECEIVING_STATE");
	},
	saveCommitsListState({ commit }: any, payload: any) {
		commit("SET_COMMITS_STATE", payload);
	},
	clearCommitsListState({ commit }: any) {
		commit("CLEAR_COMMITS_STATE");
	},
	saveAvailableInventoryState({ commit }: any, payload: any) {
		commit("SET_AVAILABLE_INVENTORY_STATE", payload);
	},
	clearAvailableInventoryState({ commit }: any) {
		commit("CLEAR_AVAILABLE_INVENTORY_STATE");
	},
	saveInventoryItemsListState({ commit }: any, payload: any) {
		commit("SET_INVENTORY_ITEMS_LIST_STATE", payload);
	},
	saveInventoryItemsViewState({ commit }: any, payload: any) {
		commit("SET_INVENTORY_ITEMS_VIEW_STATE", payload);
	},
	clearInventoryItemsState({ commit }: any) {
		commit("CLEAR_INVENTORY_ITEMS_STATE");
	},
	saveBinUtilityState({ commit }: any, payload: any) {
		commit("SET_BIN_UTILITY_STATE", payload);
	},
	clearBinUtilityState({ commit }: any) {
		commit("CLEAR_BIN_UTILITY_STATE");
	},
	saveSizeChartsState({ commit }: any, payload: any) {
		commit("SET_SIZE_CHARTS_STATE", payload);
	},
	clearSizeChartsState({ commit }: any) {
		commit("CLEAR_SIZE_CHARTS_STATE");
	},
	saveColorPaletteState({ commit }: any, payload: any) {
		commit("SET_COLOR_PALETTE_STATE", payload);
	},
	clearColorPaletteState({ commit }: any) {
		commit("CLEAR_COLOR_PALETTE_STATE");
	},
	savePackageCategoryState({ commit }: any, payload: any) {
		commit("SET_PACKAGE_CATEGORY_STATE", payload);
	},
	clearPackageCategoryState({ commit }: any) {
		commit("CLEAR_PACKAGE_CATEGORY_STATE");
	},
	saveSubItemCategoryState({ commit }: any, payload: any) {
		commit("SET_SUB_ITEM_CATEGORY_STATE", payload);
	},
	clearSubItemCategoryState({ commit }: any) {
		commit("CLEAR_SUB_ITEM_CATEGORY_STATE");
	},
	saveCustomersState({ commit }: any, payload: any) {
		commit("SET_CUSTOMERS_STATE", payload);
	},
	clearCustomersState({ commit }: any) {
		commit("CLEAR_CUSTOMERS_STATE");
	},
	saveVendorsState({ commit }: any, payload: any) {
		commit("SET_VENDORS_STATE", payload);
	},
	clearVendorsState({ commit }: any) {
		commit("CLEAR_VENDORS_STATE");
	},
};

const getters = {
	quotesListState: (state: ListState) => state.quotes,
	ordersListState: (state: ListState) => state.orders,
	purchaseOrdersListState: (state: ListState) => state.purchaseOrders,
	poGenerateState: (state: ListState) => state.poGenerate,
	findRelatedOrdersState: (state: ListState) => state.findRelatedOrders,
	supplierState: (state: ListState) => state.supplier,
	receivingListState: (state: ListState) => state.receiving,
	commitsListState: (state: ListState) => state.commits,
	availableInventoryState: (state: ListState) => state.availableInventory,
	inventoryItemsListState: (state: ListState) => state.inventoryItems.list,
	inventoryItemsViewState: (state: ListState) => state.inventoryItems.view,
	binUtilityState: (state: ListState) => state.binUtility,
	sizeChartsState: (state: ListState) => state.sizeCharts,
	colorPaletteState: (state: ListState) => state.colorPalette,
	packageCategoryState: (state: ListState) => state.packageCategory,
	subItemCategoryState: (state: ListState) => state.subItemCategory,
	customersState: (state: ListState) => state.customers,
	vendorsState: (state: ListState) => state.vendors,
	navigationDirection: (state: ListState) => state.navigation,
};

export default {
	namespaced: true,
	state,
	mutations,
	actions,
	getters,
};
